import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { t } from '../lib/i18n';
import { formatRelative } from '../ui/format';
import { ChatIcon } from './nav/icons';
import { usePreferences } from './preferences-store';
import { playMessageSound, playNotificationSound } from './sounds';
import { useRealtimeReconnect, useRealtimeTopic } from './realtime-store';
import { useAuth } from '../auth/auth-store';
import { listConversations } from '../inbox/api';
import { listChatConversations } from '../chat/api';

/**
 * Waiting customers, from anywhere in the product (Phase 148).
 *
 * The chime for an arriving message lived inside the inbox screen, so it only ever rang for
 * somebody already looking at the inbox — which is the one moment they do not need telling. A
 * person working a deal, writing a proposal or on the dashboard heard nothing, and found the
 * message whenever they next opened the screen.
 *
 * Mounted in the top bar, so it is alive on every route: the count, the list, and the sound. The
 * sound is tied to the unread total **rising**, not to the socket frame, because `message.created`
 * does not say which direction the message went and nobody wants the product to beep at them for
 * pressing send.
 *
 * Two kinds of waiting, in one place and told apart. A customer on a channel and a colleague in the
 * internal chat are both "somebody wrote to you", so splitting them across two icons means watching
 * two corners of the screen. They are not the same urgency though, and the sounds already encode
 * that difference: the rising pair for a customer, the single softer note for the team — so which
 * one it is can be heard without looking.
 */

/**
 * The floor under the socket. Fifteen minutes, deliberately slower than the notification bell's
 * five: this is a dozen rows of conversation, on every tab, for every user in every tenant.
 */
const POLL_MS = 15 * 60_000;

/** Enough to fill the panel; the rest is what the inbox screen is for. */
const SHOWN = 8;

export function MessagesBell(): React.JSX.Element {
  const client = useQueryClient();
  const navigate = useNavigate();
  const panel = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const { preferences } = usePreferences();
  const { user } = useAuth();

  const feed = useQuery({
    queryKey: ['inbox-unread'],
    // `mine` would be wrong: a conversation nobody has picked up is exactly the one worth a sound.
    queryFn: () => listConversations({ view: 'all', status: 'OPEN', limit: 50 }),
    refetchInterval: POLL_MS,
  });

  const chat = useQuery({
    queryKey: ['chat-unread'],
    queryFn: listChatConversations,
    refetchInterval: POLL_MS,
  });

  const refresh = (): void => {
    void client.invalidateQueries({ queryKey: ['inbox-unread'] });
  };

  const refreshChat = (): void => {
    void client.invalidateQueries({ queryKey: ['chat-unread'] });
  };

  const refreshBoth = (): void => {
    refresh();
    refreshChat();
  };

  useRealtimeTopic('inbox', refresh);
  useRealtimeTopic(user ? `chat_user:${user.id}` : null, refreshChat);
  useRealtimeReconnect(refreshBoth);

  const rows = (feed.data?.conversations ?? []).filter((row) => row.unreadCount > 0);
  const unread = rows.reduce((sum, row) => sum + row.unreadCount, 0);

  const chatRows = (chat.data?.conversations ?? []).filter((row) => row.unread > 0);
  const chatUnread = chatRows.reduce((sum, row) => sum + row.unread, 0);

  const total = unread + chatUnread;

  /*
   * The chime.
   *
   * `lastUnread` starts at the first total this tab ever sees rather than at zero, so opening the
   * product to a queue of waiting messages does not greet somebody with a noise for a backlog they
   * already knew about. Only a rise after that is somebody writing now.
   */
  const lastUnread = useRef<number | null>(null);
  const lastChatUnread = useRef<number | null>(null);

  useEffect(() => {
    if (feed.data === undefined) return;

    const previous = lastUnread.current;

    lastUnread.current = unread;

    if (previous === null) return;
    if (unread > previous && preferences.sounds) playMessageSound();
  }, [unread, feed.data, preferences.sounds]);

  // The team's own chime: the softer single note, so a colleague is not mistaken for a customer.
  useEffect(() => {
    if (chat.data === undefined) return;

    const previous = lastChatUnread.current;

    lastChatUnread.current = chatUnread;

    if (previous === null) return;
    if (chatUnread > previous && preferences.sounds) playNotificationSound();
  }, [chatUnread, chat.data, preferences.sounds]);

  // Clicking anywhere else closes it, which is what every other panel in this bar does.
  useEffect(() => {
    if (!open) return;

    const close = (event: MouseEvent): void => {
      if (!panel.current?.contains(event.target as Node)) setOpen(false);
    };

    document.addEventListener('mousedown', close);

    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  return (
    <div className="bell" ref={panel}>
      <button
        type="button"
        className="topbar__action"
        aria-label={t('shell.messages')}
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        <ChatIcon />
        {total > 0 && (
          <span className="bell__count" aria-hidden>
            {total > 9 ? '9+' : total}
          </span>
        )}
      </button>

      {open && (
        <div className="bell__panel" role="dialog" aria-label={t('shell.messages')}>
          <header className="bell__head">
            <span className="strong">{t('shell.messages')}</span>

            <button
              type="button"
              className="button button--quiet button--compact"
              onClick={() => {
                setOpen(false);
                navigate('/atendimento');
              }}
            >
              {t('shell.messagesAll')}
            </button>
          </header>

          {total === 0 ? (
            <p className="muted small bell__empty">{t('shell.messagesEmpty')}</p>
          ) : (
            <>
              {/* Customers first: they are the ones with a clock running on them. */}
              {rows.length > 0 && (
                <>
                  <p className="bell__section muted small">{t('shell.messagesCustomers')}</p>
                  <ul className="bell__list">
                    {rows.slice(0, SHOWN).map((row) => (
                      <li key={row.id} className="bell__item--unread">
                        <button
                          type="button"
                          className="bell__item"
                          onClick={() => {
                            setOpen(false);
                            // The id travels in the URL so the inbox opens on this thread.
                            navigate(`/atendimento?conversa=${encodeURIComponent(row.id)}`);
                          }}
                        >
                          <span>
                            {row.peerName ?? row.peerAddress}
                            {row.unreadCount > 1 && (
                              <span className="muted small"> · {row.unreadCount}</span>
                            )}
                          </span>
                          <span className="muted small">{formatRelative(row.lastMessageAt)}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </>
              )}

              {chatRows.length > 0 && (
                <>
                  <p className="bell__section muted small">{t('shell.messagesTeam')}</p>
                  <ul className="bell__list">
                    {chatRows.slice(0, SHOWN).map((room) => (
                      <li key={room.id} className="bell__item--unread">
                        <button
                          type="button"
                          className="bell__item"
                          onClick={() => {
                            setOpen(false);
                            navigate(`/chat?sala=${encodeURIComponent(room.id)}`);
                          }}
                        >
                          <span>
                            {room.name ?? t('chat.direct')}
                            {room.unread > 1 && (
                              <span className="muted small"> · {room.unread}</span>
                            )}
                          </span>
                          <span className="muted small">
                            {formatRelative(room.last_message_at)}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
